import * as Sentry from "@sentry/browser";
import type { SeverityLevel } from "@sentry/types";
import { debounce, isEqual, last, omit } from "lodash";
import { Node as ProseMirrorNode } from "prosemirror-model";
import { EditorState, Plugin, PluginKey, Transaction } from "prosemirror-state";
import { Step, Transform } from "prosemirror-transform";
import uuid from "uuid";

import { Queue } from "~/lib/collections";
import { decodeGlobalId } from "~/lib/relayUtil";
import { addBreadcrumb } from "~/lib/utils";

import { addBlocker } from "../../history";
import WebsocketClient, { GroupEmitter, WSTimeoutError } from "../../websocket";
import { Extension } from "../extension";

// Copied from prosemirror-collab
export class Rebaseable {
	step: Step;
	inverted: Step;
	sending: boolean;
	docAfter: ProseMirrorNode;
	constructor(step: Step, inverted: Step, endDoc: ProseMirrorNode) {
		this.step = step;
		this.inverted = inverted;
		this.sending = false;
		this.docAfter = endDoc;
	}
}

export function rebaseSteps(steps: Rebaseable[], over: Step[], transform: Transform) {
	for (let i = steps.length - 1; i >= 0; i--) transform.step(steps[i].inverted);
	for (let i = 0; i < over.length; i++) transform.step(over[i]);
	const result = [];
	for (let i = 0, mapFrom = steps.length; i < steps.length; i++) {
		const mapped = steps[i].step.map(transform.mapping.slice(mapFrom));
		mapFrom--;
		if (mapped && !transform.maybeStep(mapped).failed) {
			// @ts-ignore
			transform.mapping.setMirror(mapFrom, transform.steps.length - 1);
			result.push(new Rebaseable(mapped, mapped.invert(transform.docs[transform.docs.length - 1]), transform.doc));
		}
	}
	return result;
}

export interface SelectionInfo {
	from: number;
	to: number;
	headPos: number;
	hasFocus: boolean;
}

class RemoteTransaction {
	steps: Step[];
	sessionId: string;
	baseVersion: number;
	selection: SelectionInfo | null;
	constructor(steps: Step[], sessionId: string, baseVersion: number, selection: SelectionInfo | null = null) {
		this.steps = steps;
		this.sessionId = sessionId;
		this.baseVersion = baseVersion;
		this.selection = selection;
	}
}

class RemoteTransactionStorage {
	transactions = new Queue<RemoteTransaction>();

	addTransaction = (baseVersion: number, steps: Step[], sessionId: string, selection: SelectionInfo | null = null) => {
		this.transactions.put(new RemoteTransaction(steps, sessionId, baseVersion, selection));
		return this.transactions;
	};

	pop = (): RemoteTransaction => {
		const res = this.transactions.get();
		if (res) return res;
		throw new Error("poping from empty remote transactions");
	};

	clear = () => {
		this.transactions = new Queue();
	};

	get empty() {
		for (const tr of this.transactions) {
			if (tr.steps.length) return false;
		}
		return true;
	}

	get isValid() {
		if (this.transactions.isEmpty) return true;
		let i = this.transactions.head.baseVersion;
		for (const transaction of this.transactions) {
			if (transaction.baseVersion !== i) return false;
			i += transaction.steps.length;
		}
		return true;
	}
}

type websocketRemoteDeltaMessage = {
	type: "delta";
	baseVersion: number;
	steps: { [key: string]: unknown }[];
	length: number;
	sessionId: string;
	selection: SelectionInfo | null;
};

type websocketInitializeMessage = {
	type: "initialize";
	doc: { [key: string]: unknown };
	version: number;
	reinitialize?: boolean;
};

type confirmationResponse =
	| {
			accepted: true;
			serverVersion: number;
			stepsLength: number;
	  }
	| {
			accepted: false;
			serverVersion?: number;
			stepsLength: 0;
			error?: string;
	  };

type websocketConfirmationResponse = { type: "confirmation_resp"; id: string } & confirmationResponse;

type websocketMessage =
	| websocketRemoteDeltaMessage
	| websocketInitializeMessage
	| websocketConfirmationResponse
	| { type: "_other" };

/**
 * Passed as additional information on statuschange events.
 */
export interface StatusChange {
	/**
	 * `true` if all local changes have been confirmed by the server, otherwise false.
	 */
	saved: boolean;

	/**
	 * `true` if the websocket is considered connected. Temporary socket disconnects will not cause this
	 * state to change unless the connection fails for longer than 10 seconds (reconnecting-websocket's default).
	 */
	online: boolean;

	/**
	 * Last time that this note was saved.
	 */
	lastSaveTime: number;
}

export const CollabPluginKey = new PluginKey<Collab>("collab");
interface InflightConfirmation {
	ts: number;
}

export class Collab extends Extension {
	remoteTransactionStorage: RemoteTransactionStorage = new RemoteTransactionStorage();
	lastServerVersion = 0;
	unconfirmedSteps: Rebaseable[] = [];
	enabled = true;
	socket: GroupEmitter | null = null;
	lastSaveTime = 0;
	restarting = false;
	private baseVersionRejectionCount: Map<number, number> = new Map();
	private inflightConfirmationIds: Map<string, InflightConfirmation> = new Map();

	cancelNavBlock: () => void = addBlocker(() => this.onNavigate());

	static readonly CONFIRMATION_TIMEOUT = 3000;
	static readonly MAX_CONFIRMATION_ATTEMPT = 10;

	get sending() {
		const now = new Date().getTime();
		return [...this.inflightConfirmationIds.values()].some(c => c.ts + Collab.CONFIRMATION_TIMEOUT > now);
	}

	get name() {
		return "collab";
	}

	get plugins() {
		return [
			new Plugin({
				props: {
					editable: (_state: EditorState) => this.enabled,
				},

				key: CollabPluginKey,
				historyPreserveItems: true,

				state: {
					init: (_config, _state: EditorState) => {
						return this;
					},
					apply: (tr, value, _oldState, _newState) => {
						if (tr.docChanged && !tr.docs[0].eq(tr.doc)) {
							this.addStepsFromTransaction(tr);
						}
						return value;
					},
				},
			}),
		];
	}

	private onSocketMessage = (message: websocketMessage) => {
		if (message.type === "initialize") {
			this.onInitialize(message);
		} else if (message.type === "delta") {
			this.onReceivingDelta(message);
		}
		if (message.type === "confirmation_resp") {
			this.onConfirmationResp(message);
		}
	};

	private onConfirmationResp = (message: websocketConfirmationResponse) => {
		// clean up inflightConfirmationIds status before anything else
		if (!this.inflightConfirmationIds.has(message.id)) {
			return;
		}
		// this current request should be removed from inflight for sure
		this.inflightConfirmationIds.delete(message.id);
		if (message.accepted) {
			// If accepted, remove all other inflight ones too. (They are surely rejected due to version mismatch)
			this.inflightConfirmationIds.clear();
		} else {
			// I think logic is cleaner this way
			// eslint-disable-next-line no-lonely-if
			if (typeof message.serverVersion !== "undefined" && message.serverVersion > this.lastServerVersion) {
				// This current one is rejected due to version mismatch; All others will be rejected too
				this.inflightConfirmationIds.clear();
			}
		}

		if (message.accepted) {
			this.baseVersionRejectionCount.delete(this.lastServerVersion);
			this.lastSaveTime = new Date().getTime();
			this.lastServerVersion = message.serverVersion;
			this.unconfirmedSteps = this.unconfirmedSteps.slice(message.stepsLength);
			addBreadcrumb({
				category: "collab",
				message: `Change accepted. newV:${this.lastServerVersion}`,
				level: "info" as SeverityLevel,
			});
		} else {
			const rejectedCount = this.baseVersionRejectionCount.get(this.lastServerVersion) || 0;
			addBreadcrumb({
				category: "collab",
				message: `rejected change. v:${this.lastServerVersion} rc:${rejectedCount}`,
				level: "info" as SeverityLevel,
			});
			if (message.serverVersion === undefined || message.serverVersion > this.lastServerVersion) {
				this.baseVersionRejectionCount.set(this.lastServerVersion, rejectedCount + 1);
				this.debouncedSendUnconfirmed();
				if (rejectedCount > 10) {
					return this.restart();
				}
				if (rejectedCount > 5) {
					return this.ensureRemoteSteps(true).then(this.rebase);
				}
			}
		}
		if (!message.accepted && message.error) {
			console.error(message.error);
			this.unconfirmedSteps = [];
			return this.restart();
		}
		if (this.unconfirmedSteps.length) {
			this.debouncedSendUnconfirmed();
		} else {
			this.dispatchStatusChange();
		}
	};

	private onReceivingDelta = (message: websocketRemoteDeltaMessage) => {
		const schema = this.editorView && this.editorView.state.schema;
		if (!schema) return;
		this.remoteTransactionStorage.addTransaction(
			message.baseVersion,
			message.steps.map(s => Step.fromJSON(schema, s)),
			message.sessionId,
			message.selection,
		);
		this.rebase();
	};

	// NOTE: This is only used in restart
	private onInitialize = async (message: websocketInitializeMessage) => {
		if (!this.editorView) return;
		let state = this.editorView.state;
		let doc = ProseMirrorNode.fromJSON(state.schema, message.doc);
		const trDoc = new Transform(doc);

		this.restarting = true;

		if (message.reinitialize) {
			this.remoteTransactionStorage.clear();
			if (this.unconfirmedSteps.length && this.socket) {
				try {
					const requestingLastServerVersion = this.lastServerVersion;
					let { steps } = await this.socket.request<{ steps: { [key: string]: unknown; userId: number }[] }>(
						{
							type: "history",
							start: requestingLastServerVersion,
						},
						10000,
						2,
					);
					// The remote version might be greater than `message.version` at this point,
					// but we only care about making it up-to-date with `message.version` here
					steps = steps.slice(0, message.version - requestingLastServerVersion); // Discard steps after message.version
					const [, remoteSteps] = this.cleanHistorySteps(steps);
					// The `doc` is in the state where the `remoteSteps` are applied and `unconfirmedSteps` are not applied
					// `state.doc` is with `unconfirmedSteps` applied but `remoteSteps` not applied
					if (remoteSteps.length) {
						const tr = new Transform(state.doc);
						this.unconfirmedSteps = rebaseSteps(
							this.unconfirmedSteps,
							remoteSteps.map(s => Step.fromJSON(state.schema, s)),
							tr,
						);
					}
					for (const step of this.unconfirmedSteps) {
						trDoc.step(step.step);
					}
				} catch (error) {
					Sentry.captureException(error);
					console.error(error);
					this.unconfirmedSteps = [];
				}
			} else {
				this.unconfirmedSteps = [];
			}
		}
		this.editorView.updateState(
			EditorState.create({
				doc: trDoc.doc,
				plugins: state.plugins,
			}),
		);
		this.lastServerVersion = message.version;
		this.restarting = false;
		this.enabled = true;
		this.dispatchStatusChange();
		this.debouncedSendUnconfirmed();
		addBreadcrumb({ category: "collab", message: `restarted. v: ${message.version}`, level: "info" as SeverityLevel });
	};

	get connectionState(): StatusChange {
		return {
			saved: this.unconfirmedSteps.length == 0,
			lastSaveTime: this.lastSaveTime,
			online: this.socket !== null && this.socket.online,
		};
	}

	/**
	 * Dispatch an event notifying of a change in saving or connectivity status.
	 */
	private dispatchStatusChange() {
		this.dispatchEvent(
			new CustomEvent<StatusChange>("statuschange", {
				detail: this.connectionState,
			}),
		);
	}

	restart = () => {
		if (!this.editorView || !this.socket) return;
		this.inflightConfirmationIds.clear();
		this.baseVersionRejectionCount.clear();
		this.enabled = false;
		this.editorView.dispatch(this.editorView.state.tr);
		this.socket.send({ type: "reinitialize" });
	};

	addStepsFromTransaction = (tr: Transaction) => {
		if (tr.getMeta("remoteChange")) return;
		for (let i = 0; i < tr.steps.length; i++) {
			const docAfter = i < tr.steps.length - 1 ? tr.docs[i + 1] : tr.doc;
			this.addStepToUnconfirmed(new Rebaseable(tr.steps[i], tr.steps[i].invert(tr.docs[i]), docAfter));
		}
		this.debouncedSendUnconfirmed();
	};

	addStepToUnconfirmed = (rebaseableStep: Rebaseable) => {
		const lastUnconfirmed = last(this.unconfirmedSteps);
		if (!lastUnconfirmed || lastUnconfirmed.sending) {
			this.unconfirmedSteps.push(rebaseableStep);
			if (this.unconfirmedSteps.length == 1) {
				this.dispatchStatusChange();
			}
		} else {
			const newLastUnconfirmedStep = lastUnconfirmed.step.merge(rebaseableStep.step);
			const newLastUnconfirmedInverted = rebaseableStep.inverted.merge(lastUnconfirmed.inverted);
			if (newLastUnconfirmedInverted && newLastUnconfirmedStep) {
				lastUnconfirmed.step = newLastUnconfirmedStep;
				lastUnconfirmed.inverted = newLastUnconfirmedInverted;
			} else {
				this.unconfirmedSteps.push(rebaseableStep);
				if (this.unconfirmedSteps.length == 1) {
					this.dispatchStatusChange();
				}
			}
		}
	};

	/**
	 *  Removes self steps from history. Has side effect of slicing this.unconfirmedSteps
	 * @param steps steps retrieved from history
	 */
	cleanHistorySteps = (
		steps: { [key: string]: unknown; userId: number }[],
	): [number, { [key: string]: unknown; userId: number }[]] => {
		addBreadcrumb({
			category: "collab",
			level: "info" as SeverityLevel,
			message: `Clearing history steps. remote_len=${steps.length}. local_len=${this.unconfirmedSteps.length}`,
		});
		let selfStepsCount = 0;
		for (const step of steps) {
			// Because
			// 1. some edits are done on the server side
			// 2. one user can have multiple active sessions
			// The self-steps count on the server can be larger than local steps.
			if (this.unconfirmedSteps[selfStepsCount] === undefined) {
				break;
			}
			if (
				step.userId === this.options.userId &&
				isEqual(omit(step, ["userId"]), this.unconfirmedSteps[selfStepsCount].step.toJSON())
			) {
				selfStepsCount += 1;
			} else {
				break;
			}
		}
		if (selfStepsCount) {
			this.unconfirmedSteps = this.unconfirmedSteps.slice(selfStepsCount);
			return [selfStepsCount, steps.slice(selfStepsCount)];
		}
		return [0, steps];
	};

	/**
	 * Ensures that the remote transaction storage has contiguous steps starting from `this.lastServerVersion`.
	 */
	ensureRemoteSteps = async (force = false) => {
		const schema = this.editorView && this.editorView.state.schema;
		if (!this.socket || !schema) return;
		// this is to make sure we have continuous remote steps starting from `this.lastServerVersion`
		if (
			!force &&
			(this.remoteTransactionStorage.empty ||
				(this.remoteTransactionStorage.transactions.head.baseVersion === this.lastServerVersion &&
					this.remoteTransactionStorage.isValid))
		) {
			return;
		}
		const { lastServerVersion } = this;
		try {
			let { steps } = await this.socket.request<{ steps: { [key: string]: unknown; userId: number }[] }>(
				{ type: "history", start: lastServerVersion },
				3000,
				2,
			);
			steps = steps.slice(this.lastServerVersion - lastServerVersion);
			let [selfStepsCount, remoteSteps] = this.cleanHistorySteps(steps);
			this.lastServerVersion += selfStepsCount;
			this.remoteTransactionStorage.clear();
			if (remoteSteps.length) {
				this.remoteTransactionStorage.addTransaction(
					lastServerVersion,
					remoteSteps.map(s => Step.fromJSON(schema, s)),
					"",
				);
			}
		} catch (error) {
			if (!(error instanceof WSTimeoutError)) {
				Sentry.captureException(error);
			}
			console.error(error);
			this.restart();
		}
	};

	rebase = () => {
		if (this.remoteTransactionStorage.empty) {
			this.debouncedSendUnconfirmed();
			this.dispatchStatusChange();
			return;
		}
		if (this.restarting) {
			this.dispatchStatusChange();
			return;
		}
		this.ensureRemoteSteps().then(() => {
			if (!this.editorView) return;
			while (!this.remoteTransactionStorage.empty) {
				const tr = this.editorView.state.tr;
				tr.setMeta("remoteChange", true);
				const remoteTransaction = this.remoteTransactionStorage.pop();

				// This change was added by a different session, so don't store it in the undo history.
				if (remoteTransaction.sessionId !== this.options.sessionId) {
					tr.setMeta("addToHistory", false);
				}

				if (remoteTransaction.baseVersion !== this.lastServerVersion) {
					break;
				}
				if (this.unconfirmedSteps.length) {
					this.unconfirmedSteps = rebaseSteps(this.unconfirmedSteps, remoteTransaction.steps, tr);
				} else {
					remoteTransaction.steps.forEach(step => step && tr.step(step));
				}
				this.lastServerVersion = remoteTransaction.baseVersion + remoteTransaction.steps.length;
				tr.setMeta("updateSessionCursor", {
					sessionId: remoteTransaction.sessionId,
					selection: { ...remoteTransaction.selection, hasFocus: true },
				});
				this.editorView.dispatch(tr);
			}
			this.debouncedSendUnconfirmed();
		});
	};

	sendUnconfirmed = async () => {
		if (this.sending) return;
		if (!this.unconfirmedSteps.length) return;
		if (!this.remoteTransactionStorage.empty) return this.rebase();
		if (!this.socket) return;
		if (!this.socket.online) return;
		if (!this.editorView) return;
		const stepsToSend = this.unconfirmedSteps.slice();
		// avoid sending intermedia state, where block ids are not yet set by blockId plugin
		const sendable = (() => {
			let res = true;
			stepsToSend[stepsToSend.length - 1].docAfter.descendants(node => {
				if (!res) return;
				if (typeof node.attrs["blockId"] != "undefined") {
					res = !!node.attrs["blockId"];
				}
				return false;
			});
			return res;
		})();
		if (!sendable) return;
		stepsToSend.forEach(item => (item.sending = true));
		const selection = this.editorView.state.selection;
		// Sending confirmation message through websocket
		const id = uuid.v4();
		this.socket.send({
			type: "confirmation",
			steps: stepsToSend.map(item => item && item.step.toJSON()),
			baseVersion: this.lastServerVersion,
			selection: { from: selection.from, to: selection.to, headPos: selection.head },
			// This will tell the backend to broadcast the delta changes even for this session
			// We use this when we have the same note rendered more than once and we want to reflect
			// The changes in one to another.
			// TODO: The proper way to address this would be to have session ids be unique per collab
			// instances.
			broadcastDeltaBackToSender: this.options.broadcastDeltaBackToSender,
			id,
		});
		this.inflightConfirmationIds.set(id, { ts: new Date().getTime() });
		// If the `id` is still inflight after timeout, retry.
		// (No response received or not cancelled due to a previous confirmation with the same baseVersion being accepted )
		window.setTimeout(() => {
			if (this.inflightConfirmationIds.get(id)) {
				if (this.inflightConfirmationIds.size >= Collab.MAX_CONFIRMATION_ATTEMPT) {
					addBreadcrumb({
						category: "collab",
						level: "info" as SeverityLevel,
						message: `Max confirmation attempt reached. v=${this.lastServerVersion}`,
					});
					this.restart();
					return;
				}
				this.debouncedSendUnconfirmed();
			}
		}, Collab.CONFIRMATION_TIMEOUT);
	};

	debouncedSendUnconfirmed = debounce(this.sendUnconfirmed, 250, { maxWait: 750 });

	private onWindowUnload = (ev: BeforeUnloadEvent) => {
		if (this.unconfirmedSteps.length) {
			ev.preventDefault();
			ev.returnValue = "";
			return "";
		}
		return undefined;
	};

	private onNavigate = (): string | undefined => {
		if (this.unconfirmedSteps.length) {
			return "You have unsaved changes in notes. Changes you made may not be saved.";
		}
		return undefined;
	};

	init() {
		window.addEventListener("beforeunload", this.onWindowUnload);
		if (this.editorView && this.options.initialData) {
			this.lastServerVersion = this.options.initialData.version;
		}
		const [, pk] = decodeGlobalId(this.options.documentId);
		this.socket = WebsocketClient.getInstance().getOrJoinGroup(`prose_data.${pk}`, {
			sessionId: this.options.sessionId,
			publicLinkId: this.options.publicLinkId,
			inviteId: this.options.inviteId,
		});
		const init = () => {
			this.enableEditor();
			if (this.options.initialData) {
				this.ensureRemoteSteps(true).then(() => {
					this.rebase();
				});
			} else {
				this.restart();
			}
		};
		if (this.socket.online) {
			init();
		}
		const { socket } = this;

		socket.on("offline", this.onSocketOffline);
		socket.on("close", this.onSocketClose);

		this.socket.on<websocketMessage>("message", this.onSocketMessage);
		this.socket.once("online", () => {
			this.dispatchStatusChange();
			init();
		});
	}

	enableEditor = () => {
		if (!this.enabled) {
			this.enabled = true;
			this.editorView?.dispatch(this.editorView.state.tr);
		}
	};

	// every time we receive an open event, we were disconnected, try to see if we missed any messages.
	// We don't notify users here.
	onOpen = () => {
		this.ensureRemoteSteps().then(() => {
			this.rebase();
			this.enableEditor();
		});
	};

	onSocketClose = () => {
		this.socket?.off("open", this.onOpen);
		this.socket?.once("open", this.onOpen);
	};

	onSocketOffline = () => {
		if (!this.socket) return;
		this.dispatchStatusChange();
		this.socket.once("online", () => {
			this.dispatchStatusChange();
			this.ensureRemoteSteps().then(() => {
				this.rebase();
			});
		});
	};

	destroy() {
		this.cancelNavBlock();
		window.removeEventListener("beforeunload", this.onWindowUnload);
		this.debouncedSendUnconfirmed.flush();
		if (this.socket) {
			this.socket.off("message", this.onSocketMessage);
			this.socket.off("offline", this.onSocketOffline);
			this.socket.off("close", this.onSocketClose);
			this.socket.close();
			this.socket = null;
		}
	}
}

export default Collab;
